/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useEffect, useRef, useState } from "react";
import { IoCameraReverseSharp } from "react-icons/io5";
import styled from "styled-components";
import { Room as RoomType } from "twilio-video";
import { useFullpageLoading } from "../../hooks/useFullPageLoading";
import { useLocalParticipant } from "../../hooks/useLocalParticipant";
import { showToast } from "../../utils/showToast";
import { changeCamera } from "../helpers/changeCamera";
import ActionRowButton from "./ActionRowButton";

const Wrapper = styled("div")`
  display: flex;
  align-items: center;
  z-index: 2;
`;

interface IChangeCameraButtonProps {
  room: RoomType;
}

const ChangeCameraButton = ({ room }: IChangeCameraButtonProps) => {
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const selectedCameraIndex = useRef(0);

  const { handleShowFullPageLoading } = useFullpageLoading();
  const { videoOff } = useLocalParticipant();

  // Busca pelos dispositivos e filtra pelos de entrada de vídeo (câmeras)
  const getCameras = () => {
    navigator.mediaDevices.enumerateDevices().then((mediaDevices) => {
      const videoDevices = mediaDevices.filter(
        (device) => device.kind === "videoinput"
      );

      let filteredDevices: MediaDeviceInfo[] = [];
      if (videoDevices.length > 1) {
        filteredDevices = [videoDevices[0], videoDevices[videoDevices.length - 1]];
      } else if (videoDevices.length === 1) {
        filteredDevices = [videoDevices[0]];
      }

      setCameras(filteredDevices);

      const selectedDevice = localStorage.getItem("SELECTED_VIDEO_DEVICE");
      if (selectedDevice) {
        const foundIndex = filteredDevices
          .map((device) => device.deviceId)
          .indexOf(selectedDevice);
        selectedCameraIndex.current = foundIndex !== -1 ? foundIndex : 0;
      } else {
        selectedCameraIndex.current = 0;
      }
    }).catch((error) => {
      console.error("Error enumerating devices:", error);
    });
  };

  // Adiciona um listener pela mudança de dispositivos, para caso de o usuário conectar ou desconectar uma câmera
  useEffect(() => {
    navigator.mediaDevices.addEventListener("devicechange", getCameras);

    getCameras();

    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", getCameras);
    };
  }, []);

  // Troca a camera do participante local
  const handleChangeCamera = () => {
    handleShowFullPageLoading(true);
    const hasNextDevice = cameras[selectedCameraIndex.current + 1];
    const nextDevice = hasNextDevice ?? cameras[0];

    // Chama função que troca a câmera do participante local
    changeCamera(room.localParticipant, nextDevice.deviceId)
      .then((localCameraTrack) => {
        if (videoOff) localCameraTrack.stop();
        selectedCameraIndex.current = hasNextDevice
          ? selectedCameraIndex.current + 1
          : 0;
        handleShowFullPageLoading(false);
      })
      .catch(() => {
        showToast("error", {
          text: "Erro ao trocar de câmera, verifique se o dispositivo está conectado corretamente.",
          timer: 4000,
        });
        handleShowFullPageLoading(false);
      });
  };

  return (
    <Wrapper>
      <ActionRowButton
        tooltip={
          cameras.length > 1
            ? "Alterar câmera"
            : "É necessário conectar mais de um dispositivo para poder trocar de câmera"
        }
        disabled={cameras.length <= 1}
        handleClick={handleChangeCamera}
        Icon={IoCameraReverseSharp}
        iconSize={32}
      />
    </Wrapper>
  );
};

export default ChangeCameraButton;
